'use client';

import React, { useCallback } from 'react';
import { useTheme } from '@/shared/theme';

/**
 * Props interface for custom Switch component
 */
interface SwitchProps {
  /** Whether the switch is checked (on) or not */
  checked: boolean;
  /** Callback fired when switch state changes */
  onChange: (checked: boolean) => void;
  /** Whether the switch is disabled */
  disabled?: boolean;
  /** Size variant of the switch */
  size?: 'small' | 'default';
  /** Optional CSS class name */
  className?: string;
  /** Optional inline styles */
  style?: React.CSSProperties;
  /** Accessible label for screen readers */
  'aria-label'?: string;
  /** Optional loading state */
  loading?: boolean;
}

/**
 * Custom Switch component with clean, minimal design
 *
 * Provides a toggle switch with smooth animations and theme integration.
 * Designed specifically for the trading platform's aesthetic requirements.
 *
 * @example
 * // Basic usage
 * <Switch checked={autoRefresh} onChange={setAutoRefresh} />
 *
 * @example
 * // With label and size
 * <Switch
 *   checked={enabled}
 *   onChange={setEnabled}
 *   size="small"
 *   aria-label="Enable notifications"
 *   disabled={loading}
 * />
 */
export const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  size = 'default',
  className,
  style,
  'aria-label': ariaLabel,
  loading = false
}) => {
  const { token, isDark } = useTheme();
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  // Handle click/keyboard interaction
  const handleToggle = useCallback(() => {
    if (!disabled && !loading) {
      onChange(!checked);
    }
  }, [checked, onChange, disabled, loading]);

  // Handle keyboard events
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleToggle();
    }
  }, [handleToggle]);

  // Size-specific dimensions
  const dimensions = size === 'small'
    ? { width: 28, height: 14, handleSize: 10, handleOffset: 2 }
    : { width: 36, height: 18, handleSize: 14, handleOffset: 2 };

  // Calculate handle position
  const handleLeft = checked
    ? dimensions.width - dimensions.handleSize - dimensions.handleOffset
    : dimensions.handleOffset;

  const motionMs = prefersReducedMotion ? '0.01ms' : '200ms';

  // Switch container styles with Mechagora theme integration
  const switchStyles: React.CSSProperties = {
    position: 'relative',
    display: 'inline-block',
    width: `${dimensions.width}px`,
    height: `${dimensions.height}px`,
    borderRadius: `${dimensions.height / 2}px`,
    background: checked
      ? '#D4AF37'
      : '#ffffff',
    border: checked
      ? '1px solid #D4AF37'
      : '1px solid #e5e7eb',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    transition: `background-color ${motionMs} ease, box-shadow ${motionMs} ease, border-color ${motionMs} ease`,
    opacity: disabled ? 0.4 : 1,
    outline: 'none',
    boxShadow: checked
      ? '0 1px 3px rgba(212, 175, 55, 0.2)'
      : '0 1px 2px rgba(0, 0, 0, 0.05)',
    ...style
  };

  // Switch handle styles with enhanced visibility
  const handleStyles: React.CSSProperties = {
    position: 'absolute',
    top: `${dimensions.handleOffset}px`,
    left: `${handleLeft}px`,
    width: `${dimensions.handleSize}px`,
    height: `${dimensions.handleSize}px`,
    borderRadius: '50%',
    backgroundColor: checked
      ? '#ffffff'
      : isDark
        ? '#ffffff'
        : '#ffffff',
    border: checked
      ? '1px solid rgba(212, 175, 55, 0.2)'
      : isDark
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : '1px solid rgba(0, 0, 0, 0.1)',
    boxShadow: checked
      ? '0 2px 6px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(212, 175, 55, 0.1)'
      : isDark
        ? '0 2px 6px rgba(0, 0, 0, 0.4), 0 1px 3px rgba(0, 0, 0, 0.2)'
        : '0 2px 6px rgba(0, 0, 0, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: `left ${motionMs} cubic-bezier(0.4, 0, 0.2, 1), box-shadow ${motionMs} ease`,
    pointerEvents: 'none'
  };

  // Hover styles
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  // Apply hover and focus effects with Mechagora colors
  if (isHovered && !disabled && !loading) {
    if (checked) {
      switchStyles.background = '#E6C547';
      switchStyles.boxShadow = '0 2px 8px rgba(212, 175, 55, 0.3)';
    } else {
      switchStyles.background = '#f8f9fa';
      switchStyles.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
    }
  }

  if (isFocused) {
    switchStyles.boxShadow = checked
      ? '0 0 0 3px rgba(212, 175, 55, 0.3)'
      : '0 0 0 3px rgba(212, 175, 55, 0.2)';
  }

  return (
    <div
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      className={className}
      style={switchStyles}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    >
      <div style={handleStyles} />

      {/* Loading indicator — static under prefers-reduced-motion */}
      {loading && (
        <div
          className="loading-spinner"
          aria-hidden
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '10px',
            height: '10px',
            border: `1px solid ${token.colorWhite}`,
            borderTop: prefersReducedMotion
              ? `1px solid ${token.colorWhite}`
              : '1px solid transparent',
            borderRadius: '50%',
            opacity: prefersReducedMotion ? 0.85 : 1,
            animation: prefersReducedMotion
              ? 'none'
              : 'switch-spin 1s linear infinite',
          }}
        />
      )}

      {!prefersReducedMotion && (
        <style jsx>{`
          @keyframes switch-spin {
            0% { transform: translate(-50%, -50%) rotate(0deg); }
            100% { transform: translate(-50%, -50%) rotate(360deg); }
          }
        `}</style>
      )}
    </div>
  );
};

export default Switch;
